// The one diet control, shared by the profile card, the onboarding step and
// the schedule builder's per-build override.
//
// Three tiles and one helper line rather than a label under each tile: the
// descriptions ("No meat, poultry or seafood") are the length that makes a
// three-column grid wrap to three lines on the 480px onboarding card, and a
// control the user has to decipher is worse than one that explains only the
// option they are actually on.
//
// Colours come from the brand palette only — the selected state is the same
// RED / RED_LIGHT / BORDER chip treatment the builder's time and meal-count
// controls already use, so "selected" looks the same everywhere.

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { RED, RED_LIGHT, BORDER, MUTED } from "assets/theme/brandColors";

function DietSelector({ value, options, onChange, note }) {
  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${options.length}, 1fr)`,
          gap: 1,
        }}
      >
        {options.map((option) => {
          const active = option.value === value;
          return (
            <Box
              key={option.value}
              component="button"
              type="button"
              aria-pressed={active}
              onClick={() => onChange(option.value)}
              data-testid={`diet-option-${option.value}`}
              sx={{
                border: "1.5px solid",
                borderColor: active ? RED : BORDER,
                bgcolor: active ? RED_LIGHT : "#fff",
                borderRadius: "10px",
                padding: "12px 8px",
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.12s",
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                // #222 on white is 15.9:1 and RED on RED_LIGHT is 6.7:1 —
                // both clear AA comfortably at this size.
                color: active ? RED : "#222",
                fontFamily: "Roboto, sans-serif",
                lineHeight: 1.3,
                "&:hover": active ? {} : { borderColor: RED },
              }}
            >
              {option.label}
            </Box>
          );
        })}
      </Box>

      {(selected?.description || note) && (
        <Box
          sx={{
            mt: 1.25,
            fontSize: 12,
            color: MUTED,
            lineHeight: 1.5,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {note || selected.description}
        </Box>
      )}
    </Box>
  );
}

DietSelector.propTypes = {
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  // Overrides the selected option's description — used where the surrounding
  // screen needs to say something more specific (the builder's "this week only").
  note: PropTypes.string,
};

DietSelector.defaultProps = { note: null };

export default DietSelector;
